import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useCart } from "../../contexts/CartContext";
import { useTenant } from "../../contexts/TenantContext";
import { menuAPI } from "../../services/api";
import toast from "react-hot-toast";

import StoreDefaultTemplate from "./templates/store/StoreDefaultTemplate";
import StoreMinimalTemplate from "./templates/store/StoreMinimalTemplate";
import StoreBoldTemplate from "./templates/store/StoreBoldTemplate";
import StoreVibrantTemplate from "./templates/store/StoreVibrantTemplate";
import { t } from '../../i18n'

const STORE_TEMPLATES = {
  default: StoreDefaultTemplate,
  minimal: StoreMinimalTemplate,
  bold: StoreBoldTemplate,
  vibrant: StoreVibrantTemplate,
};

// The ecommerce vertical's product catalog. Products are stored in the same
// menu_items/categories tables the restaurant vertical uses (a "product" is
// just a menu item without table/kitchen framing) - only the storefront
// presentation differs, via its own STORE_TEMPLATES set. Clicking into a
// product goes to its own page (ProductDetailPage) rather than a modal;
// this page only handles browsing + the grid's quick add-to-cart.
export default function EcommerceStorefront({ storeName }) {
  const [searchParams] = useSearchParams();
  const tenant = useTenant();
  const branch = tenant.id || searchParams.get("branch") || "1";

  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);

  const { addItem, openDrawer } = useCart();

  // The Store Templates picker in Settings is the single source of truth for
  // layout - custom_theme (the visual editor) only ever overlays content
  // (text/images/colors) on top of it, it does not choose the template.
  const storeTemplate = tenant.settings?.store_template || 'default';

  useEffect(() => {
    loadProducts();
  }, [branch]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await menuAPI.getMenu({ branchId: parseInt(branch), catalog: 'store' });
      setMenu(response.data.categories);
    } catch (error) {
      toast.error(t("Failed to load products"));
    } finally {
      setLoading(false);
    }
  };

  // Quick-add from the grid card: adds the base product (no variant/modifier
  // picking) and opens the cart drawer, same as most ecommerce sites do for
  // a one-click add. Anyone who needs to choose an option visits the full
  // product page instead.
  const handleQuickAdd = (item) => {
    addItem(item, 1, [], "", parseInt(branch), null, null);
    toast.success(t("{name} added to cart", { name: item.name }));
    openDrawer();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">{t("Loading products...")}</p>
        </div>
      </div>
    );
  }

  const SelectedTemplate = STORE_TEMPLATES[storeTemplate] || StoreDefaultTemplate;

  return <SelectedTemplate menu={menu} addItem={handleQuickAdd} storeName={storeName} />;
}
